import React from 'react';
import { useIntl } from 'react-intl';
import { ProgressCircular } from '@americanexpress/dls-react';
import { ReportInlineModuleError } from '@americanexpress/travel-error-base';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { getAutomationCardOffersClassName } from '../../selectors/getHomeSelectors';
import { getCardOffersContentFromPersona } from '../../utils/personaUtils';
import { getCardType } from '../../utils/membershipDataUtils';
import { hasHomePageCardOffersRule } from '../../constants/featureRulesConstants';
import { MODULE_NAME } from '../../constants/modules';
import { useHomepageCardOffers } from '../../hooks/useHomepageCardOffers';
import { usePersonaApi } from '../../hooks/usePersonaApi';
import { useMembershipData } from '../../hooks/useMembershipDataHook';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { MarketingTiles } from './MarketingTiles';
import { PersonalizedContentError } from '../PersonalizedContent/PersonalizedContentError';
import { hasNonEmptyEntries } from '../../utils/hasNonEmptyEntries';
import PersonaTypeContext from '../../context/personaTypeContext';

export const MarketingTilesContainer = () => {
  const { formatMessage } = useIntl();
  const hasHomePagePersonalizedOffers = useConditionalFeature(hasHomePageCardOffersRule);
  const automationMarketingTilesId = useSelector(
    getAutomationCardOffersClassName(hasHomePagePersonalizedOffers)
  );
  const { data: membershipData, error: hasMembershipError } = useMembershipData();

  const {
    data: personaData,
    isLoading: personaLoading,
    errors: personaErrors,
  } = usePersonaApi();

  const {
    error: homepageCardOffersError,
    data: homepageCardOffersData,
    isLoading: homepageCardOffersLoading,
  } = useHomepageCardOffers();

  const displayLoadingState = personaLoading
    || homepageCardOffersLoading;

  if (displayLoadingState) {
    return (
      <div className="text-align-center pad flex">
        <ProgressCircular size="lg" className="margin-auto" />
      </div>
    );
  }

  let personalizedOffersData;
  let hasPersonalizedOfferError;
  const cardType = getCardType(membershipData);

  if (hasHomePagePersonalizedOffers && homepageCardOffersData) {
    personalizedOffersData = homepageCardOffersData.body;
    hasPersonalizedOfferError = homepageCardOffersError;
  } else {
    personalizedOffersData = getCardOffersContentFromPersona(
      personaData
    );
    hasPersonalizedOfferError = personaErrors;
  }

  const hasInsufficientPersonalizedOffers = !hasNonEmptyEntries(personalizedOffersData);

  if (hasPersonalizedOfferError || hasInsufficientPersonalizedOffers) {
    return (
      <div className="pad-responsive-extra-tb" data-testid={`${MODULE_NAME}-card-offer-error`}>
        <section className="container">
          <ReportInlineModuleError
            moduleID={MODULE_NAME}
            otherData={{
              component: 'MarketingTilesContainer',
              causes: {
                hasPersonalizedOfferError: !!hasPersonalizedOfferError,
                hasInsufficientPersonalizedOffers,
              },
            }}
          >
            <ErrorMessage
              message={formatMessage({ id: 'error.unableToLoadContentMessage' })}
            />
          </ReportInlineModuleError>
        </section>
      </div>
    );
  }

  const personaType = personaData?.personas[0].personaType;

  return (
    <div
      data-testid={`${MODULE_NAME}-homepage-card-offers-container`}
      className="pad-3-b"
    >
      <PersonaTypeContext.Provider value={{ personaType }}>
        <section
          id={`${MODULE_NAME}-homepage-card-offers`}
          data-testid={automationMarketingTilesId}
          className={clsx(
            'container pad-0-lr-md-down',
            automationMarketingTilesId
          )}
        >
          <PersonalizedContentError
            hasMembershipError={hasMembershipError}
          />
          <MarketingTiles
            personalizedOffersData={personalizedOffersData}
            cardType={cardType}
            hasMembershipError={hasMembershipError}
            membershipData={membershipData}
          />
        </section>
      </PersonaTypeContext.Provider>
    </div>
  );
};
